import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Play, Package } from 'lucide-react';
import { useProjectStore } from '../stores/projectStore';
import { useTerminalStore } from '../stores/terminalStore';
import { useBackend } from '../services/backend';
import {
  addToRunnerHistory,
  setProjectRunnerSettings,
  getProjectRunnerSettings,
} from '../lib/projectSettings';

interface NpmScript {
  name: string;
  command: string;
}

interface NpmScriptsPanelProps {
  isCollapsed: boolean;
}

export default function NpmScriptsPanel({ isCollapsed }: NpmScriptsPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const [scripts, setScripts] = useState<NpmScript[]>([]);

  const project = useProjectStore((s) => s.currentProject);
  const updateProject = useProjectStore((s) => s.updateProject);
  const backend = useBackend();

  // Load scripts from package.json when project changes
  useEffect(() => {
    if (!project?.id || !project?.root_path) {
      setScripts([]);
      return;
    }

    let cancelled = false;
    backend.files.readFile(project.id, 'package.json')
      .then((content) => {
        if (cancelled) return;
        try {
          const pkg = JSON.parse(content);
          if (pkg.scripts && typeof pkg.scripts === 'object') {
            const parsed: NpmScript[] = Object.keys(pkg.scripts)
              .map((name) => ({
                name,
                command: `npm run ${name}`,
              }));
            setScripts(parsed);
          } else {
            setScripts([]);
          }
        } catch {
          setScripts([]);
        }
      })
      .catch(() => {
        if (!cancelled) setScripts([]);
      })
      ;

    return () => { cancelled = true; };
  }, [project?.id, project?.root_path, backend]);

  const runScript = useCallback(async (script: NpmScript) => {
    if (!project?.root_path) return;

    const safePath = project.root_path;

    // Run via pending command (consumed by TerminalTab)
    useTerminalStore.getState().setPendingCommand({
      command: script.command,
      title: `npm: ${script.name}`,
      projectPath: safePath,
    });

    // Record in run history
    try {
      const updatedSettings = addToRunnerHistory(project.settings, {
        name: `npm: ${script.name}`,
        command: script.command,
      });
      await backend.projects.update(project.id, { settings: updatedSettings });
      await updateProject(project.id, { settings: updatedSettings });

      // Also add to runner configs if not already there
      const runnerSettings = getProjectRunnerSettings(updatedSettings);
      const alreadyExists = runnerSettings.configs.some((c) => c.command === script.command);
      if (!alreadyExists) {
        const newConfig = {
          id: `npm-${script.name}-${Date.now()}`,
          name: `npm: ${script.name}`,
          command: script.command,
        };
        const merged = [...runnerSettings.configs, newConfig];
        const finalSettings = setProjectRunnerSettings(updatedSettings, {
          configs: merged,
          defaultConfigId: runnerSettings.defaultConfigId ?? newConfig.id,
        });
        await backend.projects.update(project.id, { settings: finalSettings });
        await updateProject(project.id, { settings: finalSettings });
      }
    } catch {
      // Non-critical — script already ran
    }
  }, [project, backend, updateProject]);

  // Don't render if no project or no scripts
  if (!project || scripts.length === 0 || isCollapsed) return null;

  return (
    <div className="mt-2 pt-2 border-t border-base-300">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-2 w-full text-left hover:bg-base-300 rounded-lg transition-colors"
      >
        <ChevronDown className={`w-4 h-4 text-base-content/50 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        <Package className="w-4 h-4 text-base-content/50" />
        <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
          npm Scripts
        </span>
        <span className="text-xs text-base-content/40 ml-auto">{scripts.length}</span>
      </button>

      {expanded && (
        <div className="mt-1 space-y-0.5 max-h-64 overflow-y-auto">
          {scripts.map((script) => (
            <div
              key={script.name}
              className="group flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-base-300 transition-colors"
            >
              <button
                onClick={() => runScript(script)}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-success hover:text-success/80"
                title={`Run: npm run ${script.name}`}
              >
                <Play className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => runScript(script)}
                className="flex-1 text-left min-w-0"
              >
                <span className="text-xs text-base-content/80 truncate block">{script.name}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
